import { db } from '../../db';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  getDoc,
  getCountFromServer,
  doc,
  setDoc,
  addDoc
} from 'firebase/firestore';
import { City } from '@/models/City';
import { Session } from '@/models/User';
import { GoogleMapsGeocoder } from '../geocoding/GoogleMapsGeocoder';
import { createCityId } from '@strenforcement/domain/city';

export class FirestoreCityRepository {

  static _cache = {};

  constructor({ geocoder } = {}) {
    this.geocoder = geocoder || new GoogleMapsGeocoder();
  }

  async loadCities(includeNonPublic) {
    const user = await Session.waitUntilReady();
    if (!user) {
      return [];
    }

    // Staff cannot query every municipality and filter afterward: rules authorize the read itself.
    if (!user.isAdmin) {
      const cityIds = [...new Set(Array.isArray(user.cities) ? user.cities : [])];
      const cities = await Promise.all(cityIds.map(cityId => this.loadCity(cityId)));

      return cities.filter(city => includeNonPublic
        ? city.visibility && city.visibility !== 'deleted'
        : city.visibility === 'public');
    }

    const firestore = await db;
    return getDocs(query(
      collection(firestore, 'cities'),
      includeNonPublic ? where('visibility', '!=', 'deleted') : where("visibility", "==", "public")
    ))
    .then(snapshot => {
      return snapshot.docs.map(doc => new City(doc));
    });
  }

  async loadCity(cityId, useCache) {
    const user = await Session.waitUntilReady();
    const assigned = Array.isArray(user?.cities) && user.cities.includes(cityId);
    if (!user?.isAdmin && !assigned) {
      const error = new Error('You do not have access to this municipality.');
      error.status = 403;
      throw error;
    }

    if (this.constructor._cache[cityId] && useCache) {
      return Promise.resolve(this.constructor._cache[cityId]);
    }
    const firestore = await db;
    return getDoc(doc(firestore, 'cities', cityId))
      .then(docSnap => {
        let city = new City(docSnap);
        this.constructor._cache[cityId] = city;
        return city;
      });
  }

  async onCity(cityId, handler) {
    const firestore = await db;
    return onSnapshot(doc(firestore, 'cities', cityId), docSnap => {
      handler(new City(docSnap));
    });
  }

  async searchCities(queryString) {
    const results = await this.geocoder.geocodeCity(queryString);
    const firestore = await db;
    return results.map(result => {
      return new City({
        ref: doc(firestore, `cities/${createCityId(result.name, result.state)}`),
        data: {
          name: result.name,
          state: result.state,
          latlng: result.latlng,
          placeId: result.placeId
        }
      });
    });
  }

  async geocodeAddress(address, { state, locality } = {}) {
    return this.geocoder.geocodeAddress(address, { state, locality });
  }

  async updateCity(cityId, patch, { merge = true } = {}) {
    const firestore = await db;
    return setDoc(doc(firestore, 'cities', cityId), patch, { merge });
  }

  async updateCityLicenseExpirationPolicy(cityId, payload = {}) {
    const patch = {};
    if (payload.licenseExpirationType !== undefined) {
      patch.licenseExpirationType = payload.licenseExpirationType ?? null;
    }
    if (payload.licensePeriodStartMonthDay !== undefined) {
      patch.licensePeriodStartMonthDay = payload.licensePeriodStartMonthDay ?? null;
    }
    if (payload.licenseIssuedEarlyAllowanceMonths !== undefined) {
      patch.licenseIssuedEarlyAllowanceMonths = payload.licenseIssuedEarlyAllowanceMonths ?? null;
    }
    await this.updateCity(cityId, patch, { merge: true });
    return {
      city: {
        id: cityId,
        data: patch
      }
    };
  }

  async updateCityLicenseWorkflowStatuses(cityId, payload = {}) {
    const existing = await this.loadCity(cityId);
    const nextWorkflow = {
      ...(existing?._get?.('licenseWorkflow') || {})
    };
    if (Object.prototype.hasOwnProperty.call(payload, 'statuses')) {
      nextWorkflow.statuses = payload.statuses;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'defaultStatus')) {
      nextWorkflow.defaultStatus = payload.defaultStatus;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'defaultStatusCode')) {
      nextWorkflow.defaultStatusCode = payload.defaultStatusCode;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'includeBuiltinStatuses')) {
      nextWorkflow.includeBuiltinStatuses = payload.includeBuiltinStatuses;
    }

    await this.updateCity(cityId, {
      licenseWorkflow: nextWorkflow
    }, { merge: true });
    return {
      city: {
        id: cityId,
        data: {
          licenseWorkflow: nextWorkflow
        }
      }
    };
  }

  async updateCityLicenseApprovalEmailSettings(cityId, payload = {}) {
    const existing = await this.loadCity(cityId);
    const nextSettings = {
      ...(existing?._get?.('licenseApprovalEmail') || {})
    };
    if (Object.prototype.hasOwnProperty.call(payload.licenseApprovalEmail || {}, 'mode')) {
      nextSettings.mode = payload.licenseApprovalEmail.mode;
    }
    if (Object.prototype.hasOwnProperty.call(payload.licenseApprovalEmail || {}, 'customMessage')) {
      nextSettings.customMessage = payload.licenseApprovalEmail.customMessage;
    }
    if (Object.prototype.hasOwnProperty.call(payload.licenseApprovalEmail || {}, 'triggerStatusCode')) {
      nextSettings.triggerStatusCode = payload.licenseApprovalEmail.triggerStatusCode;
    }

    await this.updateCity(cityId, {
      licenseApprovalEmail: nextSettings
    }, { merge: true });
    return {
      city: {
        id: cityId,
        data: {
          licenseApprovalEmail: nextSettings
        }
      }
    };
  }

  async updateCityLicenseStatusEmails(cityId, payload = {}) {
    const nextSettings = payload.licenseStatusEmails || { rules: [] };
    await this.updateCity(cityId, {
      licenseStatusEmails: nextSettings
    }, { merge: true });
    return {
      city: {
        id: cityId,
        data: {
          licenseStatusEmails: nextSettings
        }
      }
    };
  }

  async previewCityLicenseStatusEmailDraft() {
    throw new Error('FirestoreCityRepository does not implement previewCityLicenseStatusEmailDraft');
  }

  async fetchFieldCounts(collectionName, fieldName, fieldValue, options = {}) {
    const firestore = await db;
    const col = collection(firestore, 'cities', options.cityId, collectionName);
    const queries = [getCountFromServer(col)];
    if (fieldName) {
      queries.push(getCountFromServer(query(col, where(fieldName, '==', fieldValue))));
    }
    const snapshots = await Promise.all(queries);
    return snapshots.map(snapshot => snapshot.data().count);
  }

  async fetchDefaultFieldCounts(collectionName, fieldNames, options = {}) {
    const firestore = await db;
    const col = collection(firestore, 'cities', options.cityId, collectionName);
    const queries = [getCountFromServer(col)];
    fieldNames.forEach(fieldName => {
      queries.push(getCountFromServer(query(col, where(fieldName, '!=', 'totallybogusvalue'))));
    });
    const snapshots = await Promise.all(queries);
    const counts = {};
    for (let i = 0; i < snapshots.length; i++) {
      const fieldName = i === 0 ? 'total' : fieldNames[i - 1];
      counts[fieldName] = snapshots[i].data().count;
    }
    return counts;
  }

  async sendErrorReport(cityId, errorType, errorData) {
    const payload = {
      type: errorType,
      timestamp: new Date(),
      data: errorData
    };

    const firestore = await db;
    return addDoc(collection(firestore, `cities/${cityId}/error_reports`), payload);
  }

  async queueCityCheck(cityId) {
    const firestore = await db;
    return setDoc(doc(firestore, 'cities', cityId), { status: 'queued' }, { merge: true });
  }
}
